<!--
 * A Vue component that wraps the b-form-select element of Bootstrap Vue.
 *
 *
 * Properties
 * ==========
 * - name -
 * (Optional) The text that is used as id and name for input element.
 *
 * - description -
 * (Optional) A description that appears below the input element.
 *
 * - text -
 * (Optional) The text to show on the label above the input field.
 *
 * - state -
 * (Optional) The validation state to set from parent component. state === true => Valid state
 *                                                               state === false => Invalid state
 *
 * - invalidMessage -
 * (Optional) The text to show when state is set to false (invalid).
 *
 * - labelSize -
 * (Optional) To control the label text size. Valid values are 'lg' for large and 'sm' for small.
 *
 * - maxlength -
 * (Optional) To set the maxlength of the input element.
 *
 * - value -
 * (Optional) To set the initial value of the input element.
 *
 * - autofocus -
 * (Optional) To set the autofocus property of the input element to true or false.
 *
 * - readonly -
 * (Optional) To set the readonly property of the input element to true or false.
 *
 * - type -
 * (Optional) To define the type of the input element. De default value is type text.
 *
 * - showDefaultOption -
 * (Optional) Show the default option "Selecteer" as a first option
 *
 * - textDefaultOption -
 * (Optional) Text of the default option
 *
 * - simpleMode -
 * (Optional) Shows the red border based on the state property.
 * Does not display green border.
 *
 * - extendedClass
 * (Optional) To set the class of the b-form-select element.
 *
 * Events
 * ==========
 * - selected -
 * This event is sent when user selects an option.
 * The object to sent along with this event is the selected string.
-->

<template>
    <div>
        <b-form-group
            :id="`fieldset-${name}`"
            v-show="visible"
            :description="description"
            :label="text"
            :label-class="labelClass"
            :label-for="name"
            :label-cols="lableAlignment"
            :label-size="labelSize"
            :invalid-feedback="invalidFeedback"
            :state="stateValide"
        >
            <b-form-select
                :id="name"
                :name="name"
                v-model="inputValue"
                @input="_onSelect"
                @change="$_onChange"
                :state="stateValide"
                :disabled="isDisabled"
                :required="required"
                :options="options"
                :size="size"
                v-bind:class="extendedClass"
            >
                <template v-if="showDefaultOption" v-slot:first>
                    <option :value="null">
                        {{ internalTextDefaultOption }}
                    </option>
                </template>
            </b-form-select>
        </b-form-group>
    </div>
</template>

<script>
    export default {
        name: 'x-select',
        props: {
            id: { type: String, required: false, default: 'bv-select-id' },
            name: { type: String, required: false, default: 'bv-select-name' },
            text: { type: String, required: false, default: '' },
            description: { type: String, required: false },
            state: { type: Boolean, required: false, default: null },
            invalidMessage: { type: String, required: false, default: '' },
            labelSize: { type: String, required: false, default: '' },
            size: { type: String, required: false, default: '' },
            value: { type: [String, Number, Boolean, Object], default: null },
            required: { type: Boolean, required: false },
            disabled: { type: Boolean, required: false },
            visible: { type: Boolean, required: false, default: true },
            showDefaultOption: { type: Boolean, required: false, default: true },
            textDefaultOption: {
                type: String,
                required: false,
                default: ''
            },
            options: { type: Array, required: true },
            simpleMode: { type: Boolean, required: false, default: false },
            lableAlignment: { type: String, default: null },
            labelClass: { type: String, default: 'font-weight-normal' },
            extendedClass: { type: [String, Object], default: '' },
            saveInState: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                valueModified: false,
                defaultValue: null,
                internalTextDefaultOption: null
            };
        },

        computed: {
            inputValue: {
                get() {
                    const value = this.value || this.defaultValue;
                    return value ?? null;
                },

                set(input) {
                    return input;
                }
            },

            isRequired() {
                return this.required;
            },

            isDisabled() {
                return this.disabled;
            },

            stateValide() {
                let calculatedState = null;
                if (this.simpleMode) {
                    calculatedState = this.state ? null : this.state;
                } else if (this.valueModified) {
                    calculatedState =
                        this.inputValue && this.inputValue.length > 0 && this.state;
                }
                return calculatedState;
            },

            invalidFeedback() {
                if (this.state === false) {
                    return this.invalidMessage;
                } else if (
                    this.inputValue &&
                    this.inputValue.length === 0 &&
                    this.required === true
                ) {
                    return this.$t('requiredField');
                } else {
                    return '';
                }
            }
        },
        created() {
            // Set the default value using $t()
            this.internalTextDefaultOption =
                this.textDefaultOption || this.$t('selectBoxMakeChoice');
        },
        mounted() {
            if (this.saveInState) {
                const filter = this.$store.getters.getFilterFromState(this.id);
                if (filter !== undefined) {
                    this.defaultValue = filter.value;
                }
            }
        },

        methods: {
            // Emitted with the select value changes
            _onSelect: function (input) {
                this.valueModified = true;
                this.$emit('selected', input);
                this.$emit('input', input);

                if (this.saveInState) {
                    this.defaultValue = null;
                    this.$store.commit('SET_UI_FILTERS', {
                        id: this.id,
                        value: input
                    });
                }
            },

            // Emitted with the select value changes via user interaction
            $_onChange(input) {
                this.$emit('changed', input);
            }
        }
    };
</script>

<style scoped>
select:disabled {
    cursor: no-drop;
}
</style>
